/**
 * Mirrors the `recommended_qty` computation in `get_replenishment_plan_v2`.
 * Keeps the UI and tests in lock-step with the backend order-up-to + MOQ +
 * order-multiple rounding rules, rather than reimplementing them ad hoc.
 */

import { humanize } from "./format";

export interface ReorderQuantityInput {
  available: number;
  incoming: number; // committed open-PO quantity (pending/partial only)
  reorderPoint: number;
  reorderQty: number;
  moq: number;
  orderMultiple: number;
}

export function computeRecommendedQty(input: ReorderQuantityInput): number {
  const { available, incoming, reorderPoint, reorderQty, moq, orderMultiple } = input;
  const multiple = orderMultiple > 0 ? orderMultiple : 1;
  // Order-up-to target gap, net of what is already available and committed inbound.
  const gap = reorderPoint + reorderQty - available - incoming;
  // Already covered — no reorder needed.
  if (gap <= 0) return 0;
  const target = Math.max(moq > 0 ? moq : 1, Math.max(reorderQty, gap));
  return Math.ceil(target / multiple) * multiple;
}

/**
 * Operator-facing label for a replenishment-plan `status`.
 * Mirrors the server-side status derivation in `get_replenishment_plan_v2`
 * (`stockout | reorder | low | ok`). Never leaks a raw enum to the UI.
 */
export function replenishmentStatusLabel(status: string | null | undefined): string {
  switch (status) {
    case "stockout": return "Out of stock";
    case "reorder": return "Below reorder point";
    case "low": return "Running low";
    case "ok": return "Healthy";
    default: return humanize(status) || "Unknown";
  }
}

/**
 * Operator-facing label for a replenishment `confidence` value
 * (`high | medium | low | no_velocity | no_data`).
 */
export function confidenceLabel(confidence: string | null | undefined): string {
  switch (confidence) {
    case "high": return "High confidence";
    case "medium": return "Medium confidence";
    case "low": return "Low confidence";
    case "no_velocity": return "No sales history";
    case "no_data": return "No sales data";
    default: return humanize(confidence) || "Unknown";
  }
}

/**
 * Concise plain-language reasons why a line appears in the plan / why a
 * quantity is recommended. Uses canonical plan fields only — it does NOT
 * reimplement the order-up-to calculation (that remains authoritative in the
 * backend RPC). The order reflects operator relevance: the stock trigger first,
 * then the existing inbound, then supplier constraints.
 */
export interface RecommendationReasonInput {
  status: string;
  incoming: number;
  moq: number;
  order_multiple: number;
}

export function recommendationReasons(r: RecommendationReasonInput): string[] {
  const reasons: string[] = [];
  if (r.status === "stockout") reasons.push("Out of stock");
  else if (r.status === "reorder") reasons.push("Below reorder point");
  else if (r.status === "low") reasons.push("Projected to run out within a week");
  if (r.incoming > 0) reasons.push(`${r.incoming} already on order`);
  if (r.moq > 1) reasons.push(`Minimum order ${r.moq}`);
  if (r.order_multiple > 1) reasons.push(`Order in multiples of ${r.order_multiple}`);
  return reasons;
}